'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { useBooking } from '@/contexts/BookingContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput } from '@/components/ui/phone-input'
import { Loader2, Send, CheckCircle2, User, Mail as MailIcon } from 'lucide-react'

export function ContactForm() {
  const t = useTranslations('booking.contact.form')
  const { tenantId } = useBooking()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tenantId || !name.trim() || !email.trim() || !message.trim()) return

    setSending(true)
    setError(null)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          message: message.trim(),
        }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message')
      }

      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.')
    } finally {
      setSending(false)
    }
  }

  if (sent) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 dark:bg-green-900/20 mb-6">
          <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          {t('messageSent')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
          {t('thankYou')}
        </p>
        <Button
          variant="outline"
          onClick={() => {
            setSent(false)
            setName('')
            setEmail('')
            setPhone('')
            setMessage('')
          }}
          className="min-w-[180px]"
        >
          {t('sendAnother')}
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name & Email Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="contact-name" className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('fullName')} <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <User className="h-4 w-4" />
            </div>
            <Input
              id="contact-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('placeholders.name')}
              required
              className="pl-10 h-11"
            />
          </div>
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="contact-email" className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('emailAddress')} <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <MailIcon className="h-4 w-4" />
            </div>
            <Input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('placeholders.email')}
              required
              className="pl-10 h-11"
            />
          </div>
        </div>
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <Label htmlFor="contact-phone" className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('phoneNumber')}
        </Label>
        <PhoneInput
          value={phone}
          onChange={setPhone}
        />
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="contact-message" className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('yourMessage')} <span className="text-destructive">*</span>
        </Label>
        <textarea
          id="contact-message"
          className="flex min-h-[140px] w-full rounded-lg border border-input bg-background px-4 py-3 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-transparent resize-y transition-all"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('placeholders.message')}
          required
          rows={5}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('responseTime')}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4">
          <p className="text-sm text-destructive font-medium">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      <div className="pt-2">
        <Button 
          type="submit" 
          disabled={sending || !name.trim() || !email.trim() || !message.trim()} 
          size="lg"
          className="w-full sm:w-auto min-w-[200px] h-12"
        >
          {sending ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              {t('sending')}
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-2" />
              {t('sendMessage')}
            </>
          )}
        </Button>
      </div>
    </form>
  )
}
